// src/pages/Authenticator.js

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Upload,
  AlertCircle,
  Loader,
  FileText,
  X,
  CheckCircle,
  XCircle,
  MapPin,
} from 'lucide-react';
import Navbar from '../components/navbar';
import LocationMasterPreview from './Locationmasterpreview';
import { getAuthHeaders, getUserRoleFromToken } from '../hydraApiService';

const INPUT_CLS =
  'w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 placeholder-gray-400 ' +
  'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors ' +
  'bg-white disabled:opacity-60';

const LOCATION_TYPES = ['Manufacturing', 'Distributor', 'Retailer', 'Recycler'];

const EMPTY_FORM = {
  locationId: '',
  locationType: '',
  facilityName: '',
  organizationName: '',
  addressStreet: '',
  addressCity: '',
  addressState: '',
  addressCountry: '',
  contactEmail: '',
};

function Field({ label, required, hint, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-600">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function Authenticator() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvError, setCsvError] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const userRole = getUserRoleFromToken();
  const isManufacturer = userRole === 'Manufacturer';

  const ADD_URL = 'https://dpp-api.acespireconsulting.com/api/locations/add';
  const CSV_URL = 'https://dpp-api.acespireconsulting.com/api/locations/upload-csv';

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formError) setFormError(null);
    if (formSuccess) setFormSuccess(null);
  };

  const handleAddLocation = async () => {
    setFormError(null);
    setFormSuccess(null);

    const requiredMap = {
      locationId: 'Location ID',
      locationType: 'Location Type',
      facilityName: 'Facility Name',
      organizationName: 'Organization Name',
      addressCity: 'City',
      addressCountry: 'Country',
    };
    const missing = Object.entries(requiredMap)
      .filter(([key]) => !form[key]?.trim())
      .map(([, label]) => label);

    if (missing.length) {
      setFormError(`Required: ${missing.join(', ')}`);
      return;
    }

    const locationIdPattern = /^[A-Z]{2}-[A-Z]{2}-[A-Z]{2,5}-[A-Z]{2,4}-\d{3,}$/;
    if (!locationIdPattern.test(form.locationId.trim())) {
      setFormError('Location ID format invalid. Example: IN-KA-BLR-RTL-001');
      return;
    }

    if (form.contactEmail.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(form.contactEmail.trim())) {
        setFormError('Please enter a valid contact email address.');
        return;
      }
    }

    setFormLoading(true);
    try {
      const response = await axios.post(
        ADD_URL,
        {
          locationId:       form.locationId.trim(),
          locationType:     form.locationType,
          facilityName:     form.facilityName.trim(),
          organizationName: form.organizationName.trim(),
          addressStreet:    form.addressStreet.trim(),
          addressCity:      form.addressCity.trim(),
          addressState:     form.addressState.trim(),
          addressCountry:   form.addressCountry.trim(),
          contactEmail:     form.contactEmail.trim(),
        },
        { headers: getAuthHeaders() }
      );

      const action = response.data?.data?.action;
      setFormSuccess(
        action === 'updated'
          ? `Location ${form.locationId.trim()} updated successfully.`
          : `Location ${form.locationId.trim()} created successfully.`
      );
      setForm(EMPTY_FORM);
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to add location. Please try again.'
      );
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormReset = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormSuccess(null);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const errors = [];
    const validated = files.filter((file) => {
      const isCSV = file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
      const underLimit = file.size <= 10 * 1024 * 1024;
      if (!isCSV) errors.push(`"${file.name}" is not a CSV`);
      else if (!underLimit) errors.push(`"${file.name}" exceeds 10MB`);
      return isCSV && underLimit;
    });
    setCsvError(errors.length ? errors.join(' · ') : null);
    setSelectedFiles(validated);
    setUploadError(null);
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) { setCsvError('Please select a file first.'); return; }
    setCsvLoading(true);
    setCsvError(null);
    setUploadError(null);
    try {
      const formData = new FormData();
      selectedFiles.forEach((f) => formData.append('files', f));
      const response = await axios.post(CSV_URL, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const { accepted = [], rejected = [] } = response.data?.data || {};
      if (response.data.success && !rejected.length) {
        navigate('/location-master');
      } else {
        setUploadError({ accepted, rejected });
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.data?.accepted || data?.data?.rejected) {
        setUploadError({ accepted: data.data.accepted || [], rejected: data.data.rejected || [] });
      } else {
        setCsvError(data?.message || data?.error || err.message || 'Upload failed.');
      }
    } finally {
      setCsvLoading(false);
    }
  };

  const handleCsvCancel = () => {
    setSelectedFiles([]);
    setCsvError(null);
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
      <Navbar />

      {/* Top Bar */}
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl shadow">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-600 hover:text-blue-700 text-sm"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <span className="text-base font-semibold text-blue-600">Authenticator</span>
      </div>

      <div className="p-4 sm:p-6 lg:p-8">

        {isManufacturer && (
          <div className="flex flex-col md:flex-row gap-4 md:items-stretch mb-4">

            {/* ── CARD 1: Add Location — 50% ── */}
            <div className="w-full md:w-1/2 bg-white rounded-xl shadow-md border border-gray-200 flex flex-col">
              <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100">
                <MapPin className="w-4 h-4" />
                <h2 className="text-base font-semibold text-gray-800">Add Location</h2>
                {/* <span className="ml-auto text-xs text-gray-400">
                  <span className="text-red-500">*</span> required
                </span> */}
              </div>

              <div className="p-5 flex flex-col flex-1">

                {formSuccess && (
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5 mb-4">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-800 flex-1">{formSuccess}</p>
                    <button onClick={() => setFormSuccess(null)}>
                      <X className="w-4 h-4 text-green-500 hover:text-green-700" />
                    </button>
                  </div>
                )}

                {formError && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-4">
                    <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-700 flex-1">{formError}</p>
                    <button onClick={() => setFormError(null)}>
                      <X className="w-4 h-4 text-red-400 hover:text-red-600" />
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mb-4">

                  <Field label="Location ID" required hint="e.g. IN-KA-BLR-RTL-001">
                    <input
                      name="locationId"
                      value={form.locationId}
                      onChange={handleFormChange}
                      placeholder="IN-KA-BLR-RTL-001"
                      className={INPUT_CLS}
                      disabled={formLoading}
                    />
                  </Field>

                  <Field label="Location Type" required>
                    <select
                      name="locationType"
                      value={form.locationType}
                      onChange={handleFormChange}
                      className={INPUT_CLS}
                      disabled={formLoading}
                    >
                      <option value="">Select type</option>
                      {LOCATION_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Facility Name" required>
                    <input
                      name="facilityName"
                      value={form.facilityName}
                      onChange={handleFormChange}
                      placeholder="Enter Facility Name"
                      className={INPUT_CLS}
                      disabled={formLoading}
                    />
                  </Field>

                  <Field label="Company" required>
                    <input
                      name="organizationName"
                      value={form.organizationName}
                      onChange={handleFormChange}
                      placeholder="Enter Company Name"
                      className={INPUT_CLS}
                      disabled={formLoading}
                    />
                  </Field>

                  <Field label="City" required>
                    <input
                      name="addressCity"
                      value={form.addressCity}
                      onChange={handleFormChange}
                      placeholder="Bangalore"
                      className={INPUT_CLS}
                      disabled={formLoading}
                    />
                  </Field>

                  <Field label="Country" required>
                    <input
                      name="addressCountry"
                      value={form.addressCountry}
                      onChange={handleFormChange}
                      placeholder="India"
                      className={INPUT_CLS}
                      disabled={formLoading}
                    />
                  </Field>

                  <Field label="Street Address">
                    <input
                      name="addressStreet"
                      value={form.addressStreet}
                      onChange={handleFormChange}
                      placeholder="123 Main Street"
                      className={INPUT_CLS}
                      disabled={formLoading}
                    />
                  </Field>

                  <Field label="State">
                    <input
                      name="addressState"
                      value={form.addressState}
                      onChange={handleFormChange}
                      placeholder="Karnataka"
                      className={INPUT_CLS}
                      disabled={formLoading}
                    />
                  </Field>

                  <Field label="Contact Email">
                    <input
                      type="email"
                      name="contactEmail"
                      value={form.contactEmail}
                      onChange={handleFormChange}
                      placeholder="contact@example.com"
                      className={INPUT_CLS}
                      disabled={formLoading}
                    />
                  </Field>

                </div>

                <div className="flex gap-3 pt-3 border-t border-gray-100 mt-auto">
                  <button
                    onClick={handleAddLocation}
                    disabled={formLoading}
                    className={`flex items-center gap-2 px-6 py-2 rounded text-white text-sm font-medium transition-colors ${
                      formLoading
                        ? 'bg-blue-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                    }`}
                  >
                    {formLoading
                      ? <><Loader className="w-4 h-4 animate-spin" /> Saving...</>
                      : <><MapPin className="w-4 h-4" /> Add Location</>
                    }
                  </button>
                  <button
                    onClick={handleFormReset}
                    disabled={formLoading}
                    className="px-5 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {/* ── CARD 2: CSV Import — 50% ── */}
            <div className="w-full md:w-1/2 bg-white rounded-lg shadow-md border border-gray-200">
              <div className="p-4 sm:p-6">

                <div className="flex flex-col sm:flex-row items-start justify-between mb-6 gap-4 sm:gap-0">
                  <h2 className="text-lg sm:text-xl font-normal text-gray-800">
                    Import Location Master Data
                  </h2>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 border-2 border-gray-800 rounded-lg flex items-center justify-center sm:ml-4 flex-shrink-0">
                    <Upload className="w-6 h-6 sm:w-7 sm:h-7 text-gray-800" />
                  </div>
                </div>

                {csvError && (
                  <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-700 flex-1">{csvError}</p>
                    <button onClick={() => setCsvError(null)}>
                      <X className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                )}

                {uploadError && (
                  <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 shadow-sm">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">Upload Results</h3>
                      <button onClick={() => setUploadError(null)}>
                        <X className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                    {uploadError.accepted.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs font-medium text-green-700 mb-1 flex items-center gap-1">
                          <CheckCircle className="w-3.5 h-3.5" />
                          Accepted ({uploadError.accepted.length})
                        </p>
                        <div className="space-y-1">
                          {uploadError.accepted.map((f, i) => (
                            <div key={i} className="flex items-center justify-between bg-green-50 border border-green-100 rounded px-3 py-1.5">
                              <span className="text-xs text-green-800 font-medium truncate">{f.filename}</span>
                              <span className="text-xs text-green-600 ml-2 flex-shrink-0">{f.rowCount} rows</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {uploadError.rejected.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-red-700 mb-1 flex items-center gap-1">
                          <XCircle className="w-3.5 h-3.5" />
                          Failed ({uploadError.rejected.length})
                        </p>
                        <div className="space-y-1">
                          {uploadError.rejected.map((f, i) => (
                            <div key={i} className="bg-red-50 border border-red-100 rounded px-3 py-1.5">
                              <p className="text-xs text-red-800 font-medium">{f.filename}</p>
                              <p className="text-xs text-red-600 mt-0.5">{f.message || f.error}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <label className="flex-1 w-full">
                    <input
                      type="file"
                      accept=".csv"
                      multiple
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      className="hidden"
                      id="csv-file-upload"
                      disabled={csvLoading}
                    />
                    <div className="border border-gray-300 rounded px-3 py-2 sm:py-2.5 bg-white text-gray-500 text-sm truncate">
                      {selectedFiles.length > 0 ? (
                        <span className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          {selectedFiles.length === 1
                            ? selectedFiles[0].name
                            : `${selectedFiles.length} files selected`}
                        </span>
                      ) : (
                        'No Files Chosen'
                      )}
                    </div>
                  </label>
                  <label
                    htmlFor="csv-file-upload"
                    className={`px-4 py-2 sm:py-2.5 border border-gray-300 rounded bg-white text-gray-700 cursor-pointer hover:bg-gray-50 active:bg-gray-100 text-sm whitespace-nowrap text-center transition-colors ${
                      csvLoading ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
                    }`}
                  >
                    Choose Files
                  </label>
                </div>

                {selectedFiles.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-blue-800 font-medium truncate">{file.name}</p>
                            <p className="text-xs text-blue-500">{(file.size / 1024).toFixed(2)} KB</p>
                          </div>
                        </div>
                        {!csvLoading && (
                          <button
                            onClick={() => removeFile(index)}
                            className="text-blue-400 hover:text-blue-700 ml-2 flex-shrink-0"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleUpload}
                    disabled={!selectedFiles.length || csvLoading}
                    className={`flex-1 py-2.5 sm:py-2 rounded text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                      selectedFiles.length && !csvLoading
                        ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                        : 'bg-blue-400 cursor-not-allowed'
                    }`}
                  >
                    {csvLoading
                      ? <><Loader className="w-4 h-4 animate-spin" /> Uploading...</>
                      : <><Upload className="w-4 h-4" /> Upload</>
                    }
                  </button>
                  <button
                    onClick={handleCsvCancel}
                    disabled={csvLoading}
                    className={`flex-1 py-2.5 sm:py-2 rounded bg-gray-300 text-gray-700 hover:bg-gray-400 active:bg-gray-500 text-sm font-medium transition-colors ${
                      csvLoading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    Cancel
                  </button>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Supported format:</span> CSV files only
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    <span className="font-medium">Maximum file size:</span> 10MB per file
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    <span className="font-medium">Multiple files:</span> Supported
                  </p>
                </div>

              </div>
            </div>

          </div>
        )}

        {/* Location Master Preview — always visible */}
        <LocationMasterPreview />

      </div>
    </div>
  );
}

export default Authenticator;